import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useCurrentUser } from 'src/store/user';
import { apiGetUserPersonas, apiUpdateUser } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Helmet } from 'react-helmet';
import { RadioGroup } from 'src/common/form/radio-group';
import { TextInput } from 'src/common/form/text-input';
import { apiSegmentTrackAnonymous } from 'src/utils/segment';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import OnboardingButton from '../components/layout/onboarding-button';

type UserPersona = {
  name: string;
  code: string;
};

const OTHER_PERSONA_CODE = 'other';

export const PageOnboardingPersona = () => {
  const history = useHistory();
  const { setErrorNotification } = useNotificationMessage();
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const setCurrentUser = useCurrentUser((state) => state.setCurrentUser);

  const [selectedPersona, setSelectedPersona] = useState<UserPersona['code'] | ''>('');
  const [loading, setLoading] = useState(true);
  const [personas, setPersonas] = useState<UserPersona[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await apiGetUserPersonas();
        setPersonas(response.user_personas);
      } catch (error: any) {
        setErrorNotification(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nextStep = useCallback(() => {
    if (currentOrganization.is_onboarded) {
      window.location.replace('/');
    } else {
      history.push('./story');
    }
  }, [currentOrganization.is_onboarded, history]);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      const params: Record<string, any> = {
        persona: selectedPersona,
      };

      const updatedUser = await apiUpdateUser(params);
      if (updatedUser) {
        setSubmitting(false);
        setCurrentUser(updatedUser);
        apiSegmentTrackAnonymous('User Persona Selected', {
          persona_code: selectedPersona,
          persona_name: personas.find((p) => p.code === selectedPersona)?.name || '',
          persona_other_value: selectedPersona === OTHER_PERSONA_CODE ? otherText : '',
        });
        nextStep();
      }
    } catch (error: any) {
      setSubmitting(false);
      setErrorNotification(error.message);
    }
  };

  const submitDisabled =
    !selectedPersona || (selectedPersona === OTHER_PERSONA_CODE && !otherText.trim()) || submitting;

  return (
    <>
      <Helmet>
        <title>Journey | The Storyteller</title>
      </Helmet>
      {loading ? (
        <FixedLoadingSpinner />
      ) : (
        personas?.length > 0 && (
          <div className='flex flex-col items-center w-full max-w-[500px] mx-auto'>
            <div className='text-4xl font-bold text-center mb-12 text-bedrock-black'>The Storyteller</div>
            <div className='w-full'>
              <div className='text-2xl font-semibold text-center mb-8 text-bedrock-black'>Which role best suits you?</div>
              <div className='flex flex-col gap-6'>
                <RadioGroup
                  name='persona'
                  className='flex flex-col space-y-4'
                  itemClassName='flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#1A1F71] transition-colors cursor-pointer'
                  options={personas.map(({ name, code }) => ({ label: name, value: code }))}
                  value={selectedPersona}
                  onChange={(option) => setSelectedPersona(option.value)}
                />
                {selectedPersona === OTHER_PERSONA_CODE && (
                  <TextInput
                    id='role_other'
                    placeholder='Please specify your role'
                    onChange={(e: any) => setOtherText(e.target.value)}
                    autoFocus
                    inputProps={{ 
                      value: otherText,
                      className: 'w-full p-3 border border-gray-200 rounded-lg focus:border-[#1A1F71] focus:ring-1 focus:ring-[#1A1F71]'
                    }}
                  />
                )}
              </div>
              <div className='flex mt-8'>
                <OnboardingButton
                  type='submit'
                  size='regular'
                  className='w-full py-4 text-lg font-semibold'
                  onClick={onSubmit}
                  disabled={submitDisabled}
                  label='Next'
                />
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default PageOnboardingPersona;
